import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { createContext, useContext, useEffect, useMemo, useRef, type MouseEvent, type RefObject } from "react";
import { FA6Icon } from "./FA6Icon";
import { clsx } from "clsx";
import styles from "./AlertDialog.module.css";

const alertDialogContentVariants = cva(undefined, {
	variants: {
		size: {
			xs: styles.sizeXs,
			sm: styles.sizeSm,
			md: undefined,
			lg: styles.sizeLg,
			xl: styles.sizeXl,
			full: styles.sizeFull,
		},
		variation: {
			none: undefined,
			primary: styles.variationPrimary,
			success: styles.variationSuccess,
			info: styles.variationInfo,
			warning: styles.variationWarning,
			danger: styles.variationDanger,
		},
	},
	defaultVariants: {
		size: "md",
		variation: "none",
	},
});

type AlertDialogSize = NonNullable<VariantProps<typeof alertDialogContentVariants>["size"]>;
type AlertDialogVariation = NonNullable<VariantProps<typeof alertDialogContentVariants>["variation"]>;

const AlertDialogDismissContext = createContext<{
	escapeCloseRef: RefObject<boolean>;
	requestClose: () => void;
} | null>(null);

function AlertDialogRoot({ actionsRef, onOpenChange, ...props }: AlertDialogPrimitive.Root.Props) {
	const internalActionsRef = useRef<AlertDialogPrimitive.Root.Actions | null>(null);
	const escapeCloseRef = useRef(false);

	// Keep a caller-provided actionsRef in sync with Base UI's imperative actions. Child
	// (Base UI Root) effects run before this parent effect, so internalActionsRef is populated.
	useEffect(() => {
		if (!actionsRef) return;
		actionsRef.current = internalActionsRef.current;
		return () => {
			actionsRef.current = null;
		};
	});

	const dismiss = useMemo(
		() => ({
			escapeCloseRef,
			requestClose: () => internalActionsRef.current?.close(),
		}),
		[],
	);

	return (
		<AlertDialogDismissContext.Provider value={dismiss}>
			<AlertDialogPrimitive.Root
				data-slot="alert-dialog"
				actionsRef={internalActionsRef}
				onOpenChange={(open, eventDetails) => {
					if (!open && eventDetails.reason === "escape-key" && !escapeCloseRef.current) {
						eventDetails.cancel();
						return;
					}
					onOpenChange?.(open, eventDetails);
				}}
				{...props}
			/>
		</AlertDialogDismissContext.Provider>
	);
}

function AlertDialogTrigger(props: AlertDialogPrimitive.Trigger.Props) {
	return <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />;
}

function AlertDialogClose(props: AlertDialogPrimitive.Close.Props) {
	return <AlertDialogPrimitive.Close data-slot="alert-dialog-close" {...props} />;
}

function AlertDialogPortal(props: AlertDialogPrimitive.Portal.Props) {
	return <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />;
}

function AlertDialogBackdrop({ className, ...props }: AlertDialogPrimitive.Backdrop.Props) {
	return (
		<AlertDialogPrimitive.Backdrop
			data-slot="alert-dialog-backdrop"
			className={clsx(styles.backdrop, className)}
			{...props}
		/>
	);
}

function AlertDialogContent({
	className,
	children,
	size = "md",
	variation = "none",
	showCloseIcon = false,
	backdropClose = false,
	escapeClose = false,
	...props
}: AlertDialogPrimitive.Popup.Props &
	VariantProps<typeof alertDialogContentVariants> & {
		/**
		 * Render a top-right dismiss (×) button. Off by default — an alert dialog is meant to
		 * be resolved through its footer actions, not silently dismissed.
		 */
		showCloseIcon?: boolean;
		/** Allow closing by clicking the backdrop (outside the card). Off by default. */
		backdropClose?: boolean;
		/** Allow closing with the Escape key. Off by default. */
		escapeClose?: boolean;
	}) {
	const dismiss = useContext(AlertDialogDismissContext);

	// Publish this Content's Escape preference to the Root's onOpenChange handler.
	useEffect(() => {
		if (!dismiss) return;
		dismiss.escapeCloseRef.current = escapeClose;
		return () => {
			dismiss.escapeCloseRef.current = false;
		};
	}, [dismiss, escapeClose]);

	const handleViewportMouseDown = (event: MouseEvent<HTMLDivElement>) => {
		if (backdropClose && event.button === 0 && event.target === event.currentTarget) {
			dismiss?.requestClose();
		}
	};

	return (
		<AlertDialogPortal>
			{/*
			 * forceRender because Base UI drops a nested dialog's backdrop (it assumes the parent
			 * already dims the page). Both viewports share the same fixed inset and top padding,
			 * so without it a confirmation opened from inside a Modal lands flush on the modal's
			 * card with nothing between them.
			 */}
			<AlertDialogBackdrop forceRender />
			<AlertDialogPrimitive.Viewport
				data-slot="alert-dialog-viewport"
				className={styles.viewport}
				onMouseDown={handleViewportMouseDown}
			>
				<AlertDialogPrimitive.Popup
					data-slot="alert-dialog-content"
					className={clsx(styles.content, alertDialogContentVariants({ size, variation }), className)}
					{...props}
				>
					{showCloseIcon && (
						<AlertDialogClose className={styles.close} aria-label="Close">
							<FA6Icon icon="times" variant="light" />
						</AlertDialogClose>
					)}
					{children}
				</AlertDialogPrimitive.Popup>
			</AlertDialogPrimitive.Viewport>
		</AlertDialogPortal>
	);
}

function AlertDialogHeader({ className, ...props }: React.ComponentProps<"div">) {
	return <div data-slot="alert-dialog-header" className={clsx(styles.header, className)} {...props} />;
}

/* Base UI's default Title is h2; timr's alert dialogs use an h5-sized title (17px), so render h5. */
function AlertDialogTitle({ className, render = <h5 />, ...props }: AlertDialogPrimitive.Title.Props) {
	return (
		<AlertDialogPrimitive.Title
			data-slot="alert-dialog-title"
			className={clsx(styles.title, className)}
			render={render}
			{...props}
		/>
	);
}

function AlertDialogDescription({ className, ...props }: AlertDialogPrimitive.Description.Props) {
	return (
		<AlertDialogPrimitive.Description
			data-slot="alert-dialog-description"
			className={clsx(styles.description, className)}
			{...props}
		/>
	);
}

function AlertDialogBody({ className, ...props }: React.ComponentProps<"div">) {
	return <div data-slot="alert-dialog-body" className={clsx(styles.body, className)} {...props} />;
}

function AlertDialogFooter({ className, ...props }: React.ComponentProps<"div">) {
	return <div data-slot="alert-dialog-footer" className={clsx(styles.footer, className)} {...props} />;
}

const CreateAlertDialogHandle = AlertDialogPrimitive.createHandle;

const AlertDialog = {
	Root: AlertDialogRoot,
	Trigger: AlertDialogTrigger,
	Close: AlertDialogClose,
	Content: AlertDialogContent,
	Header: AlertDialogHeader,
	Title: AlertDialogTitle,
	Description: AlertDialogDescription,
	Body: AlertDialogBody,
	Footer: AlertDialogFooter,
};

export { AlertDialog, CreateAlertDialogHandle };
export type { AlertDialogSize, AlertDialogVariation };
